import React, { lazy, Suspense } from "react";
import { useFullCurrentUser } from "@/hooks/useCurrentUser";
// @ts-ignore — .js
import { useSchoolLevels } from "@/hooks/useSchoolLevels";
// @ts-ignore — .jsx
import MainLayout from "@/layouts/dashboard";
// @ts-ignore — .jsx
import { HatLoading } from "@/components/loading-screen";

const WFNotFoundPage = lazy(() => import("@/pages/error-pages/WFNotFoundPage"));
const SignedOutNotFoundPage = lazy(
  () => import("@/pages/error-pages/SignedOutNotFoundPage"),
);
const MSNotFoundPage = lazy(() => import("@/pages/error-pages/MSNotFoundPage"));

/**
 * The element at `/404`.
 *
 * Every unmatched URL and every ProtectedRoute role or level mismatch lands
 * here, so this is the one place to pick the page. A signed-out visitor gets
 * the card alone with a way to sign in; an MS climber gets the MS 404 on its
 * own full-screen chrome; everyone else gets the WF one inside the dashboard
 * layout, whose sidebar is their real navigation.
 *
 * "MS" is the school-level test ProtectedRoute applies, so a climber it sends
 * here for being MS gets the MS page and the MS page's tiles are routes it
 * lets them into. No record once loading has finished — no token, an expired
 * one, or a failed read — is treated as signed out: the WF layout can't render
 * without a user. The choice waits for both, or it would flash the WF page at
 * an MS climber.
 */
export const NotFoundRoute = () => {
  const { activeUserRecord, userRecordsLoading } = useFullCurrentUser();
  const { matchesSchoolLevel, isLoadingSchoolLevels } = useSchoolLevels();

  if (userRecordsLoading || isLoadingSchoolLevels) return <HatLoading />;

  if (!activeUserRecord) {
    return (
      <Suspense fallback={<HatLoading />}>
        <SignedOutNotFoundPage />
      </Suspense>
    );
  }

  if (activeUserRecord.role === "climber" && matchesSchoolLevel("ms")) {
    return (
      <Suspense fallback={<HatLoading />}>
        <MSNotFoundPage />
      </Suspense>
    );
  }

  return (
    <MainLayout>
      <Suspense fallback={<HatLoading />}>
        <WFNotFoundPage />
      </Suspense>
    </MainLayout>
  );
};
